import { useEffect, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../components/ui/ToastProvider'
import { useToggleActionFollow } from '../hooks/useActionFollow'
import '../actions.css'
// `.minutes-remove-btn` (satır eylem düğmesi kalıbı) minutes.css'te tanımlı — bkz. ActionsListPanel'deki not.
import '../../minutes/minutes.css'

interface Props {
  actionId: number
  /** Görevin adı — erişilebilir ad satırı ayırt etsin diye ("… görevini takip et"). */
  name: string
  /** Sunucunun söylediği durum (`followed` alanı). */
  followed: boolean
}

/**
 * Görevi **kendi adına** takip et / takibi bırak — satırdaki yıldız (120). Dolu yıldız "takip
 * ediyorum" demektir. Görev listeleri (tablo + kart) ve Toplantı Detay'ın görev bölümü bu tek
 * bileşeni kullanır; davranış (iyimser gösterim, geri bildirim, çift tıklama koruması) tek yerde.
 *
 * <p>Çizilip çizilmeyeceği çağıranın kararıdır: kullanıcı koşulu `useCanFollowActions`, satır koşulu
 * ("kaydedilmiş **ana** görev") satırı tanıyan bileşendedir.
 *
 * <p><b>İyimser gösterim:</b> yıldız tıklanır tıklanmaz döner; liste tazelenip sunucu değeri gelince
 * (prop değişince) yerel değer bırakılır, hata olursa geri alınır. Tazeleme beklenirken düğme
 * kilitlidir — iki istek yarışıp tutarsız bir son durum bırakmasın.
 *
 * <p>Satır tıklaması detaya gider → `stopPropagation` şart (Sil düğmesiyle aynı kalıp).
 */
export default function ActionFollowButton({ actionId, name, followed }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const toggle = useToggleActionFollow()
  const [optimistic, setOptimistic] = useState<boolean | null>(null)
  // Sunucu değeri değişti (tazeleme geldi) → yerel tahmin artık gereksiz.
  useEffect(() => setOptimistic(null), [followed])

  const shown = optimistic ?? followed
  const label = t(shown ? 'actions.follow.unfollowNamed' : 'actions.follow.followNamed', {
    name: name || '—',
  })

  const onClick = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    const next = !shown
    setOptimistic(next)
    toggle.mutate(
      { id: actionId, followed: next },
      {
        onSuccess: () => toast.success(t(next ? 'actions.follow.followed' : 'actions.follow.unfollowed')),
        onError: () => {
          setOptimistic(null)
          toast.error(t('actions.follow.error'))
        },
      },
    )
  }

  return (
    <button
      type="button"
      className={`minutes-remove-btn act-follow-btn${shown ? ' act-follow-btn--on' : ''}`}
      aria-label={label}
      title={label}
      disabled={toggle.isPending}
      onClick={onClick}
    >
      <i className={shown ? 'pi pi-star-fill' : 'pi pi-star'} aria-hidden="true" />
    </button>
  )
}
